import { ImageIcon, Radio, useModeContext } from 'canoe';
import React, { ChangeEvent, useCallback, useState } from 'react';

import { useToggle } from 'commons/hooks';
import { MediaLibrary } from 'craft/components/MediaLibrary';
import { CollectionTargetLocation, TargetLocationsEnglishNames } from 'craft/types';

import {
  FallbackFieldsWrapper,
  FallbacksContainer,
  FallbacksInputToolbar,
  MediaButton,
} from './StyledComponents';
import { FallbacksEditorImplProps, VisibilityOption, VisibilityOptions } from './types';

const fallbackOptions = [
  { label: 'Hide empty elements', value: VisibilityOptions.HIDE },
  { label: 'Use fallback content', value: VisibilityOptions.SHOW },
];

export const FallbacksEditorToolbar = React.memo(
  ({
    collectionId,
    hasFallback,
    fieldData,
    fallbackMappings,
    onChangeVisibility,
    onSetImageFromLibrary,
    onBlur,
    nodeIds,
  }: FallbacksEditorImplProps) => {
    const { mode } = useModeContext();

    const [showFallbacks, onToggleFallbacks] = useToggle(hasFallback);
    const [fallbackValues, setFallbackValues] = useState<
      Record<CollectionTargetLocation, string | undefined>
    >(fallbackMappings);
    const [isMediaModalOpen, toggleMediaModal] = useToggle(false);

    const handleBlur = (targetLocation: CollectionTargetLocation) => {
      const newValue =
        targetLocation === CollectionTargetLocation.Text
          ? fallbackValues[targetLocation] || ''
          : fallbackValues[targetLocation]?.replace(/https?:\/\//, '') || '';
      setFallbackValues({
        ...fallbackValues,
        [targetLocation]: newValue,
      });
      onBlur(newValue, targetLocation);
    };

    const setImageURL = useCallback(
      (url, targetLocation) => {
        const cleanUrl = url.replace(/https?:\/\//, '');
        setFallbackValues({
          ...fallbackValues,
          [targetLocation]: cleanUrl,
        });
        onSetImageFromLibrary(targetLocation, `https://${cleanUrl}`);
      },
      [fallbackValues, onSetImageFromLibrary],
    );

    return (
      <FallbacksContainer data-cy="collection-fallbacks-container">
        <Radio
          items={fallbackOptions}
          selected={showFallbacks ? VisibilityOptions.SHOW : VisibilityOptions.HIDE}
          onChange={(value) => {
            onChangeVisibility(value as VisibilityOption);
            onToggleFallbacks();
          }}
          groupName={`fallback-${collectionId}`}
          variant="orange"
          testId="fallbacks"
        />
        {showFallbacks && (
          <FallbackFieldsWrapper data-cy="collection-fallbacks-wrapper">
            {fieldData.map(({ targetLocation }, index) => (
              <div
                key={`fallback-wrapper-${targetLocation}`}
                data-cy={`fallback-wrapper-${targetLocation}`}
              >
                <FallbacksInputToolbar
                  name="fallbackValue-toolbar"
                  value={fallbackValues?.[targetLocation] || ''}
                  placeholder={
                    targetLocation === CollectionTargetLocation.Text
                      ? 'Fallback text in case of missing data'
                      : `${TargetLocationsEnglishNames[
                          targetLocation
                        ].toLowerCase()}-in-case-of-missing-data`
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFallbackValues({
                      ...fallbackValues,
                      [targetLocation]: e.target.value,
                    })
                  }
                  onBlur={() => handleBlur(targetLocation)}
                  prefix={targetLocation === CollectionTargetLocation.Text ? undefined : 'https://'}
                  mode={mode}
                  isFirst={index === 0}
                />

                {targetLocation === CollectionTargetLocation.Image && (
                  <>
                    <MediaButton
                      appearance="outline"
                      icon={ImageIcon}
                      variant="contrast"
                      size="small"
                      fullWidth
                      onClick={toggleMediaModal}
                    >
                      Choose image
                    </MediaButton>
                    <MediaLibrary
                      nodeIds={nodeIds}
                      name="src"
                      isModalOpen={isMediaModalOpen}
                      toggleModal={toggleMediaModal}
                      setImageURL={(url) => setImageURL(url, targetLocation)}
                    />
                  </>
                )}
              </div>
            ))}
          </FallbackFieldsWrapper>
        )}
      </FallbacksContainer>
    );
  },
);
